import React, { FC, useState, useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { TranslationField } from "../../../../Components/Listing/Listing"
import useTranslationDataUtils from "../../../Utils/TranslationDataUtils"
import Api from "Vendor/Api";

export type Choice = {
    i18nKey: string
    value: string | Choice[]
}

export interface SearchableChoiceTypeProps {
    id?: string
    name?: string
    fullName?: string
    apiEndpoint?: string
    translateChoiceLabels?: {
        [key: string]: TranslationField
    }
    value?: string
    setValue?: (value: string) => void
    className?: string
    required?: boolean
    disabled?: boolean
    i18nLabel?: string
    i18nPlaceholder?: string
    i18nSearchPlaceholder?: string
    onChange?: (data: any) => void
    maxDisplayedOptions?: number
    showRemainingCount?: boolean
    searchDelay?: number
    initialChoices?: Choice[]
    allowAddNew?: boolean
    i18nAddNewLabel?: string
    onAddNew?: (newValue: string) => void
    addNewValidator?: (value: string) => boolean
    i18nAddNewPlaceholder?: string
    i18nAddNewInvalidMessage?: string
}

const SearchableChoiceType: FC<SearchableChoiceTypeProps> = ({
    id,
    name,
    fullName,
    apiEndpoint = '',
    translateChoiceLabels,
    value = '',
    setValue = () => { },
    className,
    required = false,
    disabled = false,
    i18nLabel= 'app.select',
    i18nPlaceholder = 'app.select_option',
    i18nSearchPlaceholder = 'app.search_options',
    onChange,
    maxDisplayedOptions = 50,
    showRemainingCount = true,
    searchDelay = 300,
    initialChoices = [],
    allowAddNew = false,
    i18nAddNewLabel = 'app.add_new',
    onAddNew,
    addNewValidator = (value: string) => value.trim().length > 0,
    i18nAddNewPlaceholder = 'app.enter_new_value',
    i18nAddNewInvalidMessage,
    ...rest
}) => {
    const { t } = useTranslation()
    const { translateField } = useTranslationDataUtils()
    const [isOpen, setIsOpen] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [choices, setChoices] = useState<Choice[]>(initialChoices)
    const [displayedChoices, setDisplayedChoices] = useState<Choice[]>([])
    const [loading, setLoading] = useState(false)
    const [totalCount, setTotalCount] = useState(0)
    const [showAddNewInput, setShowAddNewInput] = useState(false)
    const [newValue, setNewValue] = useState('')

    const dropdownRef = useRef<HTMLDivElement>(null)
    const searchInputRef = useRef<HTMLInputElement>(null)
    const addNewInputRef = useRef<HTMLInputElement>(null)
    const searchTimeoutRef = useRef<NodeJS.Timeout>()

    const useApiFiltering = apiEndpoint && apiEndpoint.trim().length > 0

    const searchApi = useCallback(async (search: string) => {
        if (!useApiFiltering) return

        setLoading(true)
        try {
            const searchParams = new URLSearchParams()
            if (search.trim()) {
                searchParams.append('search', search.trim())
            }

            const endpoint = searchParams.toString() ? `${apiEndpoint}?${searchParams}` : apiEndpoint
            const response = await Api.get(endpoint)

            const apiChoices: Choice[] = (response.data.sessions || []).map((session: string) => ({
                i18nKey: session,
                value: session
            }))

            setChoices(apiChoices)
            setTotalCount(apiChoices.length)
        } catch (error) {
            setChoices([])
            setTotalCount(0)
        } finally {
            setLoading(false)
        }
    }, [apiEndpoint, useApiFiltering])

    const filterChoicesLocally = useCallback((search: string) => {
        if (!search.trim()) {
            setChoices(initialChoices)
            setTotalCount(initialChoices.length)
            return
        }

        const searchLower = search.toLowerCase()
        const filtered = initialChoices.filter(choice => {
            const label = translateChoiceLabels && translateChoiceLabels.hasOwnProperty(choice.value as string)
                ? translateField(translateChoiceLabels[choice.value as string])
                : choice.i18nKey

            return label.toLowerCase().includes(searchLower) ||
                (choice.value as string).toLowerCase().includes(searchLower)
        })

        setChoices(filtered)
        setTotalCount(filtered.length)
    }, [initialChoices, translateChoiceLabels, translateField])

    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current)
        }

        searchTimeoutRef.current = setTimeout(() => {
            if (useApiFiltering) {
                searchApi(searchTerm)
            } else {
                filterChoicesLocally(searchTerm)
            }
        }, useApiFiltering ? searchDelay : 0)

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current)
            }
        }
    }, [searchTerm, searchApi, searchDelay, useApiFiltering])

    useEffect(() => {
        const limited = choices.slice(0, maxDisplayedOptions)
        setDisplayedChoices(limited)
    }, [choices, maxDisplayedOptions])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false)
                setShowAddNewInput(false)
                setNewValue('')
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    useEffect(() => {
        if (isOpen) {
            if (searchInputRef.current) {
                searchInputRef.current.focus()
            }

            if (!searchTerm) {
                if (useApiFiltering) {
                    searchApi('')
                } else {
                    filterChoicesLocally('')
                }
            }
        }
    }, [isOpen, useApiFiltering])

    useEffect(() => {
        if (showAddNewInput && addNewInputRef.current) {
            addNewInputRef.current.focus()
        }
    }, [showAddNewInput])

    useEffect(() => {
        if (!useApiFiltering && initialChoices.length > 0) {
            setChoices(initialChoices)
            setTotalCount(initialChoices.length)
        }
    }, [useApiFiltering])

    const handleOptionSelect = (optionValue: string) => {
        if (disabled) return

        setValue(optionValue)
        setIsOpen(false)
        setShowAddNewInput(false)
        setNewValue('')

        if (onChange) {
            onChange(optionValue)
        }
    }

    const handleAddNewClick = () => {
        const trimmedSearchTerm = searchTerm.trim()

        if (!addNewValidator(trimmedSearchTerm)) {
            return
        }

        setValue(trimmedSearchTerm)

        if (onAddNew) {
            onAddNew(trimmedSearchTerm)
        }

        if (onChange) {
            onChange(trimmedSearchTerm)
        }

        setIsOpen(false)
        setShowAddNewInput(false)
        setNewValue('')
    }

    const getDisplayText = () => {
        return value ? getOptionLabel(value) : t(i18nPlaceholder)
    }

    const getOptionLabel = (optionValue: string) => {
        if (translateChoiceLabels && translateChoiceLabels.hasOwnProperty(optionValue)) {
            return translateField(translateChoiceLabels[optionValue])
        }

        const found = choices.find(choice => choice.value === optionValue)
        if (found) {
            return translateChoiceLabels && translateChoiceLabels.hasOwnProperty(found.value as string)
                ? translateField(translateChoiceLabels[found.value as string])
                : t(found.i18nKey)
        }

        const foundInInitial = initialChoices.find(choice => choice.value === optionValue)

        if (foundInInitial) {
            return translateChoiceLabels && translateChoiceLabels.hasOwnProperty(foundInInitial.value as string)
                ? translateField(translateChoiceLabels[foundInInitial.value as string])
                : t(foundInInitial.i18nKey)
        }

        return optionValue
    }

    const isSelected = (optionValue: string) => {
        return value === optionValue
    }

    const totalDisplayedOptions = displayedChoices.length
    const trimmedSearchTerm = searchTerm.trim()
    const isAddNewValid = !trimmedSearchTerm || addNewValidator(trimmedSearchTerm)
    const showAddNewOption = allowAddNew && !loading && displayedChoices.length === 0 && trimmedSearchTerm.length > 0 && isAddNewValid
    const showAddNewInvalidMessage = allowAddNew && !loading && displayedChoices.length === 0 && trimmedSearchTerm.length > 0 && !isAddNewValid && !!i18nAddNewInvalidMessage

    return (
        <div className={`searchable-choice-type form-floating mb-3`}>
            {i18nLabel && (
                <label className="form-label fw-bold text-dark mb-2">
                    {t(i18nLabel)}{' '}:
                    {required && <span className="text-danger ms-1">*</span>}
                </label>
            )}
            <div className={`dropdown ${className || ''}`} ref={dropdownRef}>
                <button
                    type="button"
                    className={`btn btn-secondary w-100 text-start ${disabled ? 'disabled' : ''}`}
                    onClick={() => !disabled && setIsOpen(!isOpen)}
                    aria-expanded={isOpen}
                >
                    <span className="me-auto">{getDisplayText()}</span>
                </button>

                {isOpen && (
                    <div className="dropdown-menu show w-100 p-0 outline-black outline-1">
                        <div className="p-1 border-bottom">
                            <input
                                ref={searchInputRef}
                                type="text"
                                className="form-control"
                                placeholder={t(i18nSearchPlaceholder)}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>

                        <div className="choice-filter">
                            {loading ? (
                                <div className="dropdown-item-text text-muted d-flex align-items-center">
                                    <div className="spinner-border spinner-border-sm me-2" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    {t('app.loading')}
                                </div>
                            ) : displayedChoices.length === 0 && !showAddNewOption ? (
                                <>
                                    <div className="dropdown-item-text text-muted">
                                        {t('app.no_result')}
                                    </div>
                                    {showAddNewInvalidMessage && (
                                        <div className="dropdown-item-text text-danger">
                                            {t(i18nAddNewInvalidMessage)}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <>
                                    {displayedChoices.map((choice) => (
                                        <button
                                            key={choice.value as string}
                                            type="button"
                                            className={`dropdown-item d-flex align-items-center p-2 ${isSelected(choice.value as string) ? 'active' : ''}`}
                                            onClick={() => handleOptionSelect(choice.value as string)}
                                        >
                                            <span>
                                                {translateChoiceLabels && translateChoiceLabels.hasOwnProperty(choice.value as string)
                                                    ? translateField(translateChoiceLabels[choice.value as string])
                                                    : t(choice.i18nKey)}
                                            </span>
                                        </button>
                                    ))}

                                    {showAddNewOption && (
                                        <button
                                            type="button"
                                            className="dropdown-item d-flex align-items-center p-2 text-primary"
                                            onClick={handleAddNewClick}
                                        >
                                            <i className="fas fa-plus me-2"></i>
                                            <span>{t(i18nAddNewLabel)}: "{searchTerm}"</span>
                                        </button>
                                    )}
                                </>
                            )}
                        </div>

                        {showRemainingCount && totalCount > totalDisplayedOptions && !loading && (
                            <div className="dropdown-item-text text-muted small border-top">
                                {t('app.remaining_result', { count: totalCount - totalDisplayedOptions })}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

export default SearchableChoiceType
